import {
  Box,
  Collapse,
  Image,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  Text,
} from "@chakra-ui/react";
import ChevronDownIcon from "assets/svg/chevron-down";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

type NavGroupProps = {
  item: INavigationData;
  isOpen: boolean;
};

const NavGroup = ({ item, isOpen }: NavGroupProps) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const isActive = (value: string) => pathname.includes(value);
  const isGroupActive = isActive(item.link);
  const [isExpanded, setIsExpanded] = useState<boolean>(isGroupActive);

  useEffect(() => {
    if (isGroupActive) setIsExpanded(true);
  }, [isGroupActive]);

  const subItems = item.children ?? [];

  if (!isOpen) {
    return (
      <Menu placement="right-start" offset={[0, 36]}>
        <MenuButton aria-label={item.text} display="flex" textAlign="left">
          <Image
            src={item.icon}
            alt={item.text}
            boxSize={"25px"}
            opacity={isGroupActive ? "" : "0.7"}
          />
        </MenuButton>
        <Portal>
          <MenuList
            bgColor="#1A0044"
            borderColor="whiteAlpha.300"
            minWidth="180px"
            paddingY={2}
            zIndex={200}
          >
            <Text
              paddingX={3}
              paddingBottom={2}
              fontSize={12}
              fontWeight={600}
              color="#F7F7F8"
              opacity="0.7"
              textTransform="uppercase"
            >
              {item.text}
            </Text>
            {subItems.map((subItem) => (
              <MenuItem
                key={subItem.link}
                bgColor="transparent"
                color={isActive(subItem.link) ? "#E6FF55" : "#F7F7F8"}
                fontSize={14}
                fontWeight={500}
                _hover={{ bgColor: "whiteAlpha.100" }}
                _focus={{ bgColor: "whiteAlpha.100" }}
                onClick={() => navigate(subItem.link)}
              >
                {subItem.text}
              </MenuItem>
            ))}
          </MenuList>
        </Portal>
      </Menu>
    );
  }

  return (
    <Box>
      <Box
        display="flex"
        alignItems="center"
        gap={2}
        role="button"
        cursor="pointer"
        aria-expanded={isExpanded}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <Image
          src={item.icon}
          alt={item.text}
          boxSize={"25px"}
          opacity={isGroupActive ? "" : "0.7"}
        />
        <Text
          flex={1}
          color={isGroupActive ? "#E6FF55" : "#F7F7F8"}
          opacity={isGroupActive ? "" : "0.7"}
          fontSize={14}
          fontWeight={500}
          lineHeight={"24px"}
        >
          {item.text}
        </Text>
        <Box
          display="flex"
          opacity="0.7"
          transform={isExpanded ? "rotate(180deg)" : "none"}
          transition="transform 0.2s ease"
        >
          <ChevronDownIcon />
        </Box>
      </Box>

      <Collapse in={isExpanded} animateOpacity>
        <Box
          display="flex"
          flexDirection="column"
          gap={3}
          marginTop={4}
          paddingLeft="22px"
        >
          {subItems.map((subItem) => (
            <Box
              key={subItem.link}
              display="flex"
              alignItems="center"
              gap={3}
              role="button"
              cursor="pointer"
              onClick={() => navigate(subItem.link)}
            >
              <Box
                width="3px"
                height="18px"
                borderRadius="2px"
                bgColor={isActive(subItem.link) ? "#E6FF55" : "transparent"}
              />
              <Text
                color={isActive(subItem.link) ? "#E6FF55" : "#F7F7F8"}
                opacity={isActive(subItem.link) ? "" : "0.7"}
                fontSize={14}
                fontWeight={500}
                lineHeight={"20px"}
              >
                {subItem.text}
              </Text>
            </Box>
          ))}
        </Box>
      </Collapse>
    </Box>
  );
};

export default NavGroup;
